import React from 'react'
import {
  getAutocompleteOptions,
  getDepartmentOptions,
  getClassOptions,
  getCostOriginSourceOptions,
  getApprovalStatusOptions,
  getSupplierLocationOptions,
} from '../services/requests'
import { itemCommonUtilis } from 'item-component'

export const ROUTER_BASENAME = itemCommonUtilis.findAppPath()

export const trainingLinkUrl =
  'https://partnersonline.com/page/library/ICM/applications/Application%20Overview'

export const supportLinkUrl = 'https://www.partnersonline.com/page/help'

export const emptyResponseMessage = 'No message available'

export const TOO_MANY_RECORDS = 'TOO_MANY_RECORDS'
export const INVALID_TEMPLATE = 'INVALID_TEMPLATE'
export const DUPLICATE_NOTIFICATION = 'DUPLICATE_NOTIFICATION'

export const spreadsheetFileTypeIdentifier =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

export const NEW_COST_THRESHOLDS = {
  maxDollarPercentageChange: 50,
  maxDollarChange: 1000,
}

export const TOASTER_DEFAULTS = {
  autoHideDuration: 10000,
}

export const CUSTOM_ANALYTICS_TYPES = {
  PROMPT_SHOWN: 'IMPRESSION',
  ROUTE_NOT_FOUND: 'PAGE_NOT_FOUND',
  ERROR: 'ERROR',
  ACCESS_DENIED: 'ERROR',
  PAGE_VIEW: 'PAGE_VIEW',
}

export const CUSTOM_ANALYTICS_EVENT_NAMES = {
  COST_SUBMISSION_THRESHOLD_WARNING: 'COST_SUBMISSION_THRESHOLD_WARNING',
  ROUTE_NOT_FOUND: 'REQUESTED_ROUTE_NOT_FOUND',
  UNEXPECTED_ERROR: 'UNEXPECTED_ERROR',
  ACCESS_DENIED: 'ACCESS_DENIED',
}

export const getThresholdWarningText = () => {
  return `The cost you entered for this row has a percentage change
  greater than ${NEW_COST_THRESHOLDS.maxDollarPercentageChange}%. If this is
  intentional you can ignore this message and proceed. If this was a mistake,
  please correct it.`
}

export const getNewCostThresholdWarningText = () => {
  return `The new cost you entered is greater than $${NEW_COST_THRESHOLDS.maxDollarChange - 1}. If this is
  intentional, you can ignore this message and proceed. If this was a mistake,
  please correct it.`
}

export const getInvalidCostErrorText = () => {
  return `The cost you entered for this row isn't valid. New costs must be
  greater than 0.`
}

export const noCasepackExplanationText = `We either found no Casepack Quantity
  data or multiple conflicting Casepack Quantities for this item.  Please enter
  any cost updates for this item as a per unit cost.`

export const getCurrentUTCDateString = () => {
  const now = new Date()
  const month = now.getUTCMonth() + 1
  const day = now.getUTCDate()
  const year = now.getUTCFullYear()

  return `${month}/${day}/${year}`
}

export const getCurrentLocalDateString = () => {
  const now = new Date()
  const month = now.getMonth() + 1
  const day = now.getDate()
  const year = now.getFullYear()

  return `${month}/${day}/${year}`
}

export const convertDateToUTCDateString = (date) => {
  if (!date) {
    return null
  }
  const month = date.getUTCMonth() + 1
  const day = date.getUTCDate()
  const year = date.getUTCFullYear()

  return `${month}/${day}/${year}`
}

export const convertDateToLocalDateString = (date) => {
  if (!date) {
    return null
  }
  const month = date.getMonth() + 1
  const day = date.getDate()
  const year = date.getFullYear()

  return `${month}/${day}/${year}`
}

export const convertDateToLocalDateStringWithBackendFormatting = (date) => {
  if (!date) {
    return null
  }
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const year = date.getFullYear()

  return `${year}-${month}-${day}`
}

const formatCostDecimals = (costValue) => {
  return Number(costValue).toFixed(
    Math.max(2, (costValue.toString().split('.')[1] || []).length),
  )
}

const costRangeToString = (costRange) => {
  if (!costRange || !costRange?.minCost || !costRange?.maxCost) {
    return 'No Cost Set'
  } else if (costRange?.minCost === costRange?.maxCost) {
    return `$${formatCostDecimals(costRange.minCost)}`
  } else {
    return `$${formatCostDecimals(costRange?.minCost)} - $${formatCostDecimals(
      costRange?.maxCost,
    )}`
  }
}

const costToString = (cost) => {
  if (cost) {
    return `$${formatCostDecimals(cost)}`
  } else {
    return 'No Cost Set'
  }
}

const genericArrayToString = (array) => {
  if (Array.isArray(array)) {
    return array.join(', ')
  } else {
    return array
  }
}

const customCostSort = (sortDirection, field, tableData) => {
  if (!sortDirection) {
    return tableData
  }

  const getSortValue = (value) => {
    if (value && Object.keys(value)?.length === 0) {
      return 0
    }
    return sortDirection === 'asc' ? value?.minCost : value?.maxCost
  }

  return tableData.toSorted((a, b) => {
    const valueA = getSortValue(a[field]?.cellValue)
    const valueB = getSortValue(b[field]?.cellValue)
    return sortDirection === 'asc' ? valueA - valueB : valueB - valueA
  })
}

const locationTypeToString = (locationType) => {
  const locationTypeStringMap = {
    STR: 'Store',
    DC: 'DC',
    SUPPLIER: 'Supplier Location',
  }
  return locationTypeStringMap[locationType] || locationType
}

const dateToString = (givenDate) => {
  const date = new Date(givenDate + ' UTC')

  if (givenDate === '+999999999-12-31') {
    return '12/31/9999'
  } else if (givenDate && date instanceof Date && !isNaN(date)) {
    return date.toLocaleDateString('en-US', { timeZone: 'UTC' })
  } else {
    return givenDate
  }
}

export const dateTimeToString = (givenDate) => {
  const date = new Date(givenDate)

  if (givenDate && date instanceof Date && !isNaN(date)) {
    return date.toLocaleDateString('en-US', {
      timeZoneName: 'short',
      hour: 'numeric',
      minute: 'numeric',
    })
  } else {
    return givenDate
  }
}

export const dateTimeToShortString = (givenDate) => {
  const date = new Date(givenDate)

  if (givenDate && date instanceof Date && !isNaN(date)) {
    return date.toLocaleDateString('en-US')
  } else {
    return givenDate
  }
}

const locationTypeToLabel = (type) => {
  return FIELD_DETAILS[type]?.label || type
}

export const compareByValue = (options) => {
  return options.sort((a, b) => {
    return a.value.localeCompare(b.value, undefined, { numeric: true })
  })
}

export const APPROVAL_STATUS_ENUMS = {
  AWAITING_PROCESSING: {
    enum: 'AWAITING_PROCESSING',
    shortLabel: 'Pending',
  },
  AUTOMATICALLY_APPROVED: {
    enum: 'AUTOMATICALLY_APPROVED',
    shortLabel: 'Approved',
  },
  AUTOMATICALLY_REJECTED: {
    enum: 'AUTOMATICALLY_REJECTED',
    shortLabel: 'Rejected',
  },
  AWAITING_MANUAL_REVIEW: {
    enum: 'AWAITING_MANUAL_REVIEW',
    shortLabel: 'Pending',
  },
  APPROVED_BY_TARGET_EMPLOYEE: {
    enum: 'APPROVED_BY_TARGET_EMPLOYEE',
    shortLabel: 'Approved',
  },
  REJECTED_BY_TARGET_EMPLOYEE: {
    enum: 'REJECTED_BY_TARGET_EMPLOYEE',
    shortLabel: 'Rejected',
  },
}

export const APPROVED_APPROVAL_STATUSES = [
  APPROVAL_STATUS_ENUMS.AUTOMATICALLY_APPROVED.enum,
  APPROVAL_STATUS_ENUMS.APPROVED_BY_TARGET_EMPLOYEE.enum,
]

export const PENDING_APPROVAL_STATUSES = [
  APPROVAL_STATUS_ENUMS.AWAITING_PROCESSING.enum,
  APPROVAL_STATUS_ENUMS.AWAITING_MANUAL_REVIEW.enum,
]

export const REJECTED_APPROVAL_STATUSES = [
  APPROVAL_STATUS_ENUMS.AUTOMATICALLY_REJECTED.enum,
  APPROVAL_STATUS_ENUMS.REJECTED_BY_TARGET_EMPLOYEE.enum,
]

export const FIELD_DETAILS = {
  UPC: {
    keyName: 'UPC',
    variableName: 'upc',
    label: 'UPC',
    pluralLabel: 'UPCs',
    filterType: 'TEXT',
    regexPattern: /^\d{6,13}$/,
    tableDetails: {
      defaultColumnWidth: 125,
      cellClassName: 'hesp-general-ellipsedText',
      align: 'center',
      sortable: true,
      sortType: 'numeric',
    },
  },
  UPCS: {
    keyName: 'UPCS',
    variableName: 'upcs',
    label: 'UPC',
    pluralLabel: 'UPCs',
    filterType: 'TEXT',
    regexPattern: /^\d{6,13}$/,
    tableDetails: {
      defaultColumnWidth: 150,
      cellClassName: 'hesp-general-ellipsedText',
      align: 'center',
      toStringFunc: genericArrayToString,
      sortable: true,
      sortType: 'numeric',
    },
  },
  SECONDARY_UPCS: {
    keyName: 'SECONDARY_UPCS',
    variableName: 'secondaryUpcs',
    label: 'Secondary UPCs',
    tableDetails: {
      defaultColumnWidth: 150,
      cellClassName: 'hesp-general-ellipsedText',
      align: 'center',
      toStringFunc: genericArrayToString,
    },
  },
  CASEPACK_QUANTITY: {
    keyName: 'CASEPACK_QUANTITY',
    variableName: 'casePackQuantity',
    label: 'Casepack Qty',
    tableDetails: {
      defaultColumnWidth: 120,
      cellClassName: 'hesp-general-ellipsedText',
      align: 'center',
      sortable: true,
      sortType: 'numeric',
    },
  },
  DPCI: {
    keyName: 'DPCI',
    variableName: 'dpci',
    label: 'DPCI',
    pluralLabel: 'DPCIs',
    filterType: 'TEXT',
    regexPattern: /^\d{3}-\d{2}-\d{4}$/,
    tableDetails: {
      defaultColumnWidth: 125,
      cellClassName: 'hesp-general-ellipsedText',
      align: 'center',
      sortable: true,
      sortType: 'character',
    },
  },
  HISTORICAL_ITEMS: {
    keyName: 'HISTORICAL_ITEMS',
    variableName: 'includeHistoricalItems',
    label: 'Include Historical Items',
    filterType: 'CHECKBOX',
  },
  DPCIS: {
    keyName: 'DPCIS',
    variableName: 'dpcis',
    label: 'DPCI',
    pluralLabel: 'DPCIs',
    filterType: 'TEXT',
    regexPattern: /^\d{3}-\d{2}-\d{4}$/,
  },
  TCIN: {
    keyName: 'TCIN',
    variableName: 'tcin',
    label: 'TCIN',
    pluralLabel: 'TCINs',
    filterType: 'TEXT',
    regexPattern: /^\d{1,10}$/,
    tableDetails: {
      defaultColumnWidth: 105,
      cellClassName: 'hesp-general-ellipsedText',
      align: 'center',
      sortable: true,
      sortType: 'numeric',
    },
  },
  TCINS: {
    keyName: 'TCINS',
    variableName: 'tcins',
    label: 'TCIN',
    pluralLabel: 'TCINs',
    filterType: 'TEXT',
    regexPattern: /^\d{1,10}$/,
  },
  BRANDS: {
    keyName: 'BRANDS',
    variableName: 'brands',
    label: 'Brand',
    pluralLabel: 'Brands',
    filterType: 'AUTOCOMPLETE',
    tableDetails: {
      defaultColumnWidth: 125,
      cellClassName: 'hesp-general-ellipsedText',
      align: 'center',
      toStringFunc: genericArrayToString,
      sortable: true,
      sortType: 'character',
    },
    filterDetails: {
      autocompleteRequestName: 'BRAND',
      autocompleteRequestFunc: getAutocompleteOptions,
      requestType: 'debounce',
    },
    costChangeInsightsFilterDetails: {
      autocompleteRequestName: 'BRAND',
      autocompleteRequestFunc: getAutocompleteOptions,
      requestType: 'debounce',
    },
  },
  ITEM_NAME: {
    keyName: 'ITEM_NAME',
    variableName: 'itemName',
    label: 'Item Name',
    tableDetails: {
      defaultColumnWidth: 200,
      cellClassName: 'hesp-general-ellipsedText',
      align: 'left',
      sortable: true,
      sortType: 'character',
    },
  },
  DEPARTMENT_ID: {
    keyName: 'DEPARTMENT_ID',
    variableName: 'departmentId',
    label: 'Department',
    pluralLabel: 'Departments',
    tableDetails: {
      defaultColumnWidth: 115,
      cellClassName: 'hesp-general-ellipsedText',
      align: 'center',
      sortable: true,
      sortType: 'numeric',
    },
  },
  CLASS_ID: {
    keyName: 'CLASS_ID',
    variableName: 'classId',
    label: 'Class',
    pluralLabel: 'Classes',
    tableDetails: {
      defaultColumnWidth: 75,
      cellClassName: 'hesp-general-ellipsedText',
      align: 'center',
      sortable: true,
      sortType: 'numeric',
    },
  },
  DEPARTMENTS: {
    keyName: 'DEPARTMENTS',
    variableName: 'departments',
    label: 'Department',
    pluralLabel: 'Departments',
    filterType: 'AUTOCOMPLETE',
    filterDetails: {
      autocompleteRequestName: 'Department',
      autocompleteRequestFunc: getDepartmentOptions,
      requestType: 'singleRequest',
      sortFunc: compareByValue,
    },
    costChangeInsightsFilterDetails: {
      autocompleteRequestName: 'Department',
      autocompleteRequestFunc: getDepartmentOptions,
      requestType: 'singleRequest',
      sortFunc: compareByValue,
    },
    tableDetails: {
      defaultColumnWidth: 115,
      cellClassName: 'hesp-general-ellipsedText',
      align: 'center',
    },
  },
  DEPARTMENT_CLASS: {
    keyName: 'DEPARTMENT_CLASS',
    variableName: 'departmentClasses',
    label: 'Class',
    pluralLabel: 'Classes',
    filterType: 'AUTOCOMPLETE',
    filterDetails: {
      autocompleteRequestName: 'Class',
      autocompleteRequestFunc: getClassOptions,
      requestType: 'staticList',
      sortFunc: compareByValue,
    },
    costChangeInsightsFilterDetails: {
      autocompleteRequestName: 'Class',
      autocompleteRequestFunc: getClassOptions,
      requestType: 'staticList',
      sortFunc: compareByValue,
    },
    tableDetails: {
      defaultColumnWidth: 75,
      cellClassName: 'hesp-general-ellipsedText',
      align: 'center',
    },
  },
  STYLE: {
    keyName: 'STYLE',
    variableName: 'styles',
    label: 'Style',
    pluralLabel: 'Styles',
    filterType: 'AUTOCOMPLETE',
    filterDetails: {
      autocompleteRequestName: 'STYLE',
      autocompleteRequestFunc: getAutocompleteOptions,
      requestType: 'debounce',
    },
    costChangeInsightsFilterDetails: {
      autocompleteRequestName: 'STYLE',
      autocompleteRequestFunc: getAutocompleteOptions,
      requestType: 'debounce',
      sortable: true,
      sortType: 'character',
    },
  },
  STYLE_ID: {
    keyName: 'STYLE_ID',
    variableName: 'styleId',
    label: 'Style',
    pluralLabel: 'Styles',
    tableDetails: {
      defaultColumnWidth: 125,
      cellClassName: 'hesp-general-ellipsedText',
      align: 'center',
      sortable: true,
      sortType: 'character',
    },
  },
  COST: {
    keyName: 'COST',
    variableName: 'cost',
    label: 'Cost',
    tableDetails: {
      defaultColumnWidth: 100,
      cellClassName: 'hesp-general-ellipsedText',
      align: 'center',
      toStringFunc: costToString,
      sortable: true,
      sortType: 'numeric',
    },
  },
  COST_RANGE: {
    keyName: 'COST_RANGE',
    variableName: 'minMaxCosts',
    label: 'Current Cost(s)',
    tableDetails: {
      defaultColumnWidth: 175,
      cellClassName: 'hesp-general-ellipsedText',
      align: 'left',
      toStringFunc: costRangeToString,
      sortable: true,
      sortType: 'numeric',
      onSort: customCostSort,
    },
  },
  COST_LOCATION_GROUP_TYPE: {
    keyName: 'COST_LOCATION_GROUP_TYPE',
    variableName: 'costLocationGroupType',
    label: 'Update Level',
    tableDetails: {
      defaultColumnWidth: 140,
      cellClassName: 'hesp-general-ellipsedText',
      align: 'center',
      toStringFunc: locationTypeToLabel,
      sortable: true,
      sortType: 'character',
    },
  },
  COST_LOCATION_GROUP_VALUE: {
    keyName: 'COST_LOCATION_GROUP_VALUE',
    variableName: 'costLocationGroupValue',
    label: 'Level Value',
    tableDetails: {
      defaultColumnWidth: 125,
      cellClassName: 'hesp-general-ellipsedText',
      align: 'center',
      sortable: true,
      sortType: 'character',
    },
  },
  COST_LOCATION_GROUP_VALUES: {
    keyName: 'COST_LOCATION_GROUP_VALUES',
    variableName: 'costLocationGroupValues',
    label: 'Level Value',
    tableDetails: {
      defaultColumnWidth: 145,
      cellClassName: 'hesp-general-ellipsedText',
      align: 'center',
      toStringFunc: genericArrayToString,
      sortable: true,
      sortType: 'character',
    },
  },
  COST_LOCATION_GROUP_NAME: {
    keyName: 'COST_LOCATION_GROUP_NAME',
    variableName: 'costLocationGroupName',
    label: 'Level Name',
    tableDetails: {
      defaultColumnWidth: 125,
      cellClassName: 'hesp-general-ellipsedText',
      align: 'center',
    },
  },
  COST_ORIGIN_SOURCE: {
    keyName: 'COST_ORIGIN_SOURCE',
    variableName: 'sources',
    label: 'Cost Origin Source',
    pluralLabel: 'Cost Origin Sources',
    tableDetails: {
      defaultColumnWidth: 125,
      cellClassName: 'hesp-general-ellipsedText',
      align: 'center',
    },
    filterType: 'AUTOCOMPLETE',
    costChangeInsightsFilterDetails: {
      autocompleteRequestName: 'Source',
      autocompleteRequestFunc: getCostOriginSourceOptions,
      requestType: 'singleRequest',
    },
  },
  NEW_START_DATE_INPUT: {
    keyName: 'NEW_START_DATE_INPUT',
    variableName: 'new_start_date',
    label: 'Start Date',
    tableDetails: {
      defaultColumnWidth: 150,
      align: 'center',
    },
  },
  NEW_END_DATE_INPUT: {
    keyName: 'NEW_END_DATE_INPUT',
    variableName: 'new_end_date',
    label: 'End Date',
    tableDetails: {
      defaultColumnWidth: 150,
      align: 'center',
    },
  },
  NEW_UNIT_COST_INPUT: {
    keyName: 'NEW_UNIT_COST_INPUT',
    variableName: 'new_unit_cost',
    label: 'New Unit Cost',
    tableDetails: {
      defaultColumnWidth: 160,
      align: 'center',
    },
  },
  NEW_CASEPACK_COST_INPUT: {
    keyName: 'NEW_CASEPACK_COST_INPUT',
    variableName: 'new_casepack_cost',
    label: 'New Case Cost',
    tableDetails: {
      defaultColumnWidth: 160,
      align: 'center',
    },
  },
  LOCATION_ID: {
    keyName: 'LOCATION_ID',
    variableName: 'locationId',
    label: 'Location',
    tableDetails: {
      defaultColumnWidth: 110,
      cellClassName: 'hesp-general-ellipsedText',
      align: 'center',
      sortable: true,
      sortType: 'numeric',
    },
  },
  LOCATION_TYPE: {
    keyName: 'LOCATION_TYPE',
    variableName: 'locationTypeCode',
    label: 'Location Type',
    tableDetails: {
      defaultColumnWidth: 136,
      cellClassName: 'hesp-general-ellipsedText',
      align: 'center',
      sortable: true,
      sortType: 'character',
      toStringFunc: locationTypeToString,
    },
  },
  LAST_UPDATED_ON: {
    keyName: 'LAST_UPDATED_ON',
    variableName: 'lastUpdatedOn',
    label: 'Updated On',
    tableDetails: {
      defaultColumnWidth: 125,
      cellClassName: 'hesp-general-ellipsedText',
      align: 'center',
      toStringFunc: dateTimeToString,
      sortable: true,
      sortType: 'date',
    },
  },
  LAST_UPDATED_BY: {
    keyName: 'LAST_UPDATED_BY',
    variableName: 'lastUpdatedBy',
    label: 'Updated By',
    tableDetails: {
      defaultColumnWidth: 125,
      cellClassName: 'hesp-general-ellipsedText',
      align: 'center',
      sortable: true,
      sortType: 'character',
    },
  },
  DECISION_DATE: {
    keyName: 'DECISION_DATE',
    variableName: 'decisionOn',
    label: 'Decision Date',
    tableDetails: {
      defaultColumnWidth: 115,
      cellClassName: 'hesp-general-ellipsedText',
      align: 'center',
      toStringFunc: dateTimeToString,
    },
  },
  DECISION_REASON: {
    keyName: 'DECISION_REASON',
    variableName: 'decisionReason',
    label: 'Decision Reason',
    tableDetails: {
      defaultColumnWidth: 115,
      cellClassName: 'hesp-general-ellipsedText',
      align: 'center',
    },
  },
  SUBMITTED_ON: {
    keyName: 'SUBMITTED_ON',
    variableName: 'submittedOn',
    label: 'Submitted On',
    tableDetails: {
      defaultColumnWidth: 115,
      cellClassName: 'hesp-general-ellipsedText',
      align: 'center',
      toStringFunc: dateTimeToString,
    },
  },
  CHAIN: {
    keyName: 'CHAIN',
    variableName: 'CHAIN',
    label: 'Chain',
  },
  SUPPLIER_CHAIN: {
    keyName: 'SUPPLIER_CHAIN',
    variableName: 'SUPPLIER_CHAIN',
    label: 'Supplier Chain',
  },
  STATE: {
    keyName: 'STATE',
    variableName: 'STATE',
    label: 'State',
    pluralLabel: 'States',
    filterType: 'AUTOCOMPLETE',
    filterDetails: {
      autocompleteRequestName: 'STATE',
      autocompleteRequestFunc: getAutocompleteOptions,
      requestType: 'singleRequest',
    },
    costChangeInsightsFilterDetails: {
      autocompleteRequestName: 'STATE',
      autocompleteRequestFunc: getAutocompleteOptions,
      requestType: 'singleRequest',
    },
    tableDetails: {
      cellClassName: 'hesp-gerenal-breakWord',
    },
  },
  SUPPLIER_STATE: {
    keyName: 'SUPPLIER_STATE',
    variableName: 'SUPPLIER_STATE',
    label: 'Supplier State',
    dataTableLabel: 'State',
    pluralLabel: 'Supplier States',
    filterType: 'AUTOCOMPLETE',
    filterDetails: {
      autocompleteRequestName: 'STATE',
      autocompleteRequestFunc: getAutocompleteOptions,
      requestType: 'singleRequest',
    },
    costChangeInsightsFilterDetails: {
      autocompleteRequestName: 'STATE',
      autocompleteRequestFunc: getAutocompleteOptions,
      requestType: 'singleRequest',
    },
    tableDetails: {
      cellClassName: 'hesp-gerenal-breakWord',
    },
  },
  DC: {
    keyName: 'DC',
    variableName: 'DC',
    label: 'Distribution Center',
    pluralLabel: 'Distribution Centers',
    filterType: 'AUTOCOMPLETE',
    filterDetails: {
      autocompleteRequestName: 'DC_ID_NAME_CITY_STATE',
      autocompleteRequestFunc: getAutocompleteOptions,
      requestType: 'debounce',
    },
    costChangeInsightsFilterDetails: {
      autocompleteRequestName: 'DC_ID_NAME_CITY_STATE',
      autocompleteRequestFunc: getAutocompleteOptions,
      requestType: 'debounce',
    },
    tableDetails: {
      cellClassName: 'hesp-gerenal-breakWord',
    },
  },
  CUSTOM: {
    keyName: 'CUSTOM',
    variableName: 'CUSTOM',
    label: 'Custom Location Group',
    pluralLabel: 'Custom Location Groups',
    filterType: 'AUTOCOMPLETE',
    filterDetails: {
      requestType: 'staticList',
    },
    costChangeInsightsFilterDetails: {
      requestType: 'staticList',
    },
    tableDetails: {
      cellClassName: 'hesp-gerenal-breakWord',
    },
  },
  ERROR_CODES: {
    keyName: 'ERROR_CODES',
    variableName: 'errorCodes',
    label: 'Error Code',
    pluralLabel: 'Error Codes',
    tableDetails: {
      defaultColumnWidth: 100,
      cellClassName: 'hesp-general-ellipsedText',
      align: 'center',
      toStringFunc: genericArrayToString,
    },
  },
  ERROR_MESSAGE: {
    keyName: 'ERROR_MESSAGE',
    variableName: 'errorMessage',
    label: 'Error Message',
    pluralLabel: 'Error Messages',
    tableDetails: {
      defaultColumnWidth: 200,
      cellClassName: 'hesp-general-ellipsedText',
      align: 'center',
      toStringFunc: genericArrayToString,
    },
  },
  ERROR_ROW: {
    keyName: 'ERROR_ROW',
    variableName: 'errorRow',
    label: 'Error Row',
    pluralLabel: 'Error Rows',
    tableDetails: {
      defaultColumnWidth: 100,
      cellClassName: 'hesp-general-ellipsedText',
      align: 'center',
    },
  },
  VENDOR_ID: {
    keyName: 'VENDOR_ID',
    variableName: 'vendorId',
    label: 'Vendor',
    pluralLabel: 'Vendors',
    tableDetails: {
      defaultColumnWidth: 150,
      cellClassName: 'hesp-general-ellipsedText',
      align: 'center',
    },
  },
  START_DATE: {
    keyName: 'START_DATE',
    variableName: 'startDate',
    label: 'Start Date',
    pluralLabel: 'Start Dates',
    tableDetails: {
      defaultColumnWidth: 100,
      cellClassName: 'hesp-general-ellipsedText',
      align: 'center',
      toStringFunc: dateToString,
    },
  },
  END_DATE: {
    keyName: 'END_DATE',
    variableName: 'endDate',
    label: 'End Date',
    pluralLabel: 'End Dates',
    tableDetails: {
      defaultColumnWidth: 100,
      cellClassName: 'hesp-general-ellipsedText',
      align: 'center',
      toStringFunc: dateToString,
    },
  },
  START_DATE_BETWEEN: {
    keyName: 'START_DATE_BETWEEN',
    variableName: 'startDateBetween',
    label: 'Started Between Dates',
    pluralLabel: 'Started Between Dates',
    filterType: 'DATE_RANGE',
  },
  SUBMITTED_DATE_BETWEEN: {
    keyName: 'SUBMITTED_DATE_BETWEEN',
    variableName: 'submittedOnBetween',
    label: 'Submitted Between Dates',
    pluralLabel: 'Submitted Between Dates',
    filterType: 'DATE_RANGE',
  },
  DECIDED_DATE_BETWEEN: {
    keyName: 'DECIDED_DATE_BETWEEN',
    variableName: 'decisionOnBetween',
    label: 'Approval Decision Between Dates',
    pluralLabel: 'Approval Decision Between Dates',
    filterType: 'DATE_RANGE',
  },
  NEW_CASEPACK_COST: {
    keyName: 'NEW_CASEPACK_COST',
    variableName: 'newCasepackCost',
    label: 'New Casepack Cost',
    pluralLabel: 'New Casepack Costs',
    tableDetails: {
      defaultColumnWidth: 100,
      cellClassName: 'hesp-general-ellipsedText',
      align: 'center',
      toStringFunc: costToString,
    },
  },
  NEW_UNIT_COST: {
    keyName: 'NEW_UNIT_COST',
    variableName: 'newCost',
    label: 'New Cost',
    pluralLabel: 'New Costs',
    tableDetails: {
      defaultColumnWidth: 100,
      cellClassName: 'hesp-general-ellipsedText',
      align: 'center',
      toStringFunc: costToString,
    },
  },
  LOCATION: {
    keyName: 'LOCATION',
    variableName: 'LOCATION',
    label: 'Store Location',
    pluralLabel: 'Locations',
    filterType: 'AUTOCOMPLETE',
    filterDetails: {
      autocompleteRequestName: 'STORE_ID_NAME_CITY_STATE',
      autocompleteRequestFunc: getAutocompleteOptions,
      requestType: 'debounce',
    },
    costChangeInsightsFilterDetails: {
      autocompleteRequestName: 'STORE_ID_NAME_CITY_STATE',
      autocompleteRequestFunc: getAutocompleteOptions,
      requestType: 'debounce',
    },
    tableDetails: {
      cellClassName: 'hesp-gerenal-breakWord',
    },
  },
  SUPPLIER_LOCATION: {
    keyName: 'SUPPLIER_LOCATION',
    variableName: 'SUPPLIER_LOCATION',
    label: 'Supplier Location',
    dataTableLabel: 'Location',
    pluralLabel: 'Supplier Locations',
    filterType: 'AUTOCOMPLETE',
    filterDetails: {
      autocompleteRequestName: 'REQUEST NAME NOT NEEDED',
      autocompleteRequestFunc: getSupplierLocationOptions,
      requestType: 'singleRequest',
    },
    costChangeInsightsFilterDetails: {
      autocompleteRequestName: 'REQUEST NAME NOT NEEDED',
      autocompleteRequestFunc: getSupplierLocationOptions,
      requestType: 'staticList',
    },
    tableDetails: {
      cellClassName: 'hesp-gerenal-breakWord',
    },
  },
  EFFECTIVE_START_DATE: {
    keyName: 'EFFECTIVE_START_DATE',
    variableName: 'effectiveStartDate',
    label: 'Effective Start Date',
    tableDetails: {
      defaultColumnWidth: 115,
      cellClassName: 'hesp-general-ellipsedText',
      align: 'center',
      toStringFunc: dateToString,
    },
  },
  SOURCE: {
    keyName: 'SOURCE',
    variableName: 'source',
    label: 'Submitted Through',
  },
  SOURCE_LABEL: {
    keyName: 'SOURCE_LABEL',
    variableName: 'sourceLabel',
    label: 'Submitted Through',
  },
  APPROVAL_STATUS: {
    keyName: 'APPROVAL_STATUS',
    variableName: 'status',
    label: 'Approval Status',
    pluralLabel: 'Approval Statuses',
    tableDetails: {
      defaultColumnWidth: 125,
      cellClassName: 'hesp-general-ellipsedText',
      align: 'center',
    },
    filterType: 'AUTOCOMPLETE',
    costChangeInsightsFilterDetails: {
      autocompleteRequestName: 'ApprovalStatus',
      autocompleteRequestFunc: getApprovalStatusOptions,
      requestType: 'singleRequest',
    },
  },
  APPROVAL_STATUS_LABEL: {
    keyName: 'APPROVAL_STATUS_LABEL',
    variableName: 'statusLabel',
    label: 'Approval Status',
    pluralLabel: 'Approval Statuses',
    tableDetails: {
      defaultColumnWidth: 125,
      cellClassName: 'hesp-general-ellipsedText',
      align: 'center',
    },
  },
  APPROVED_BY: {
    keyName: 'APPROVED_BY',
    variableName: 'decisionBy',
    label: 'Approval Decision By',
    filterType: 'TEXT',
    regexPattern: /^[a-zA-Z0-9]*$/,
  },
  SUBMITTED_BY: {
    keyName: 'SUBMITTED_BY',
    variableName: 'submittedBy',
    label: 'Submitted By',
    filterType: 'TEXT',
    regexPattern: /^[a-zA-Z0-9]*$/,
  },
  ONLINE_ONLY: {
    keyName: 'ONLINE_ONLY',
    variableName: 'ONLINE_ONLY',
    label: 'Online Only',
  },
  TIMEFRAME: {
    keyName: 'TIMEFRAME',
    variableName: 'timeframe',
    label: 'Timeframe',
  },
  DATE: {
    keyName: 'DATE',
    variableName: 'date',
    label: 'Date',
    filterType: 'DATE',
  },
  VENDOR_IDS: {
    keyName: 'VENDOR_IDS',
    variableName: 'vendorIds',
    label: 'Vendor',
  },
  TIMEZONE: {
    keyName: 'TIMEZONE',
    variableName: 'timezone',
    label: 'Timezone',
  },
}

export const COST_LOCATION_GROUP_TYPE = FIELD_DETAILS.COST_LOCATION_GROUP_TYPE

export const COST_LOCATION_GROUP_VALUES =
  FIELD_DETAILS.COST_LOCATION_GROUP_VALUES

export const LOCATION_TYPE_TO_COST_LOCATION_GROUP_TYPE = {
  STR: FIELD_DETAILS?.LOCATION?.keyName,
  DC: FIELD_DETAILS?.DC?.keyName,
  SUPPLIER: FIELD_DETAILS?.SUPPLIER_LOCATION?.keyName,
}

export const GET_COST_SEARCH_DEFAULT_SELECTIONS = (isDvs = false) => ({
  [FIELD_DETAILS.UPCS.variableName]: [],
  [FIELD_DETAILS.DPCIS.variableName]: [],
  [FIELD_DETAILS.TCINS.variableName]: [],
  [FIELD_DETAILS.BRANDS.variableName]: [],
  [FIELD_DETAILS.STYLE.variableName]: [],
  [FIELD_DETAILS.DEPARTMENTS.variableName]: [],
  [FIELD_DETAILS.DEPARTMENT_CLASS.variableName]: [],
  costLocationGroup: {
    costLocationGroupType: isDvs
      ? FIELD_DETAILS.SUPPLIER_CHAIN.variableName
      : FIELD_DETAILS.CHAIN.variableName,
    costLocationGroupValues: [],
  },
  [FIELD_DETAILS.TIMEFRAME.variableName]: 'CURRENT',
  [FIELD_DETAILS.START_DATE.variableName]: null,
  [FIELD_DETAILS.END_DATE.variableName]: null,
  [FIELD_DETAILS.HISTORICAL_ITEMS.variableName]: false,
})

export const COST_SEARCH_VIEW_BY_OPTIONS = {
  [FIELD_DETAILS.CHAIN.keyName]: FIELD_DETAILS.CHAIN,
  [FIELD_DETAILS.STATE.keyName]: FIELD_DETAILS.STATE,
  [FIELD_DETAILS.DC.keyName]: FIELD_DETAILS.DC,
  [FIELD_DETAILS.CUSTOM.keyName]: FIELD_DETAILS.CUSTOM,
  [FIELD_DETAILS.LOCATION.keyName]: FIELD_DETAILS.LOCATION,
}

export const COST_SEARCH_VIEW_BY_OPTIONS_DVS = {
  [FIELD_DETAILS.SUPPLIER_CHAIN.keyName]: FIELD_DETAILS.SUPPLIER_CHAIN,
  [FIELD_DETAILS.SUPPLIER_STATE.keyName]: FIELD_DETAILS.SUPPLIER_STATE,
  [FIELD_DETAILS.SUPPLIER_LOCATION.keyName]: FIELD_DETAILS.SUPPLIER_LOCATION,
}

export const COST_SEARCH_SEARCH_CRITERIA_FIELDS = {
  [FIELD_DETAILS.UPCS.keyName]: FIELD_DETAILS.UPCS,
  [FIELD_DETAILS.DPCIS.keyName]: FIELD_DETAILS.DPCIS,
  [FIELD_DETAILS.TCINS.keyName]: FIELD_DETAILS.TCINS,
  [FIELD_DETAILS.BRANDS.keyName]: FIELD_DETAILS.BRANDS,
  [FIELD_DETAILS.DEPARTMENTS.keyName]: FIELD_DETAILS.DEPARTMENTS,
  [FIELD_DETAILS.DEPARTMENT_CLASS.keyName]: FIELD_DETAILS.DEPARTMENT_CLASS,
  [FIELD_DETAILS.HISTORICAL_ITEMS.keyName]: FIELD_DETAILS.HISTORICAL_ITEMS,
}

export const APPLIED_FILTERS_SEARCH_CRITERIA_FIELDS = {
  ...COST_SEARCH_SEARCH_CRITERIA_FIELDS,
  [FIELD_DETAILS.STYLE.keyName]: FIELD_DETAILS.STYLE,
}

export const COST_SEARCH_TIMEFRAME_OPTIONS = {
  [FIELD_DETAILS.TIMEFRAME.keyName]: FIELD_DETAILS.TIMEFRAME,
  [FIELD_DETAILS.START_DATE.keyName]: FIELD_DETAILS.START_DATE,
  [FIELD_DETAILS.END_DATE.keyName]: FIELD_DETAILS.END_DATE,
}

export const DATA_TABLE_FIELDS = {
  [FIELD_DETAILS.DPCI.keyName]: FIELD_DETAILS.DPCI,
  [FIELD_DETAILS.ITEM_NAME.keyName]: FIELD_DETAILS.ITEM_NAME,
  [FIELD_DETAILS.COST_LOCATION_GROUP_VALUE.keyName]:
    FIELD_DETAILS.COST_LOCATION_GROUP_VALUE,
  [FIELD_DETAILS.COST_RANGE.keyName]: FIELD_DETAILS.COST_RANGE,
  [FIELD_DETAILS.NEW_UNIT_COST_INPUT.keyName]:
    FIELD_DETAILS.NEW_UNIT_COST_INPUT,
  [FIELD_DETAILS.NEW_START_DATE_INPUT.keyName]:
    FIELD_DETAILS.NEW_START_DATE_INPUT,
  [FIELD_DETAILS.CASEPACK_QUANTITY.keyName]: {
    ...FIELD_DETAILS.CASEPACK_QUANTITY,
    tableDetails: {
      ...FIELD_DETAILS.CASEPACK_QUANTITY.tableDetails,
      defaultColumnWidth: 140,
    },
  },
  [FIELD_DETAILS.NEW_CASEPACK_COST_INPUT.keyName]:
    FIELD_DETAILS.NEW_CASEPACK_COST_INPUT,
  [FIELD_DETAILS.TCIN.keyName]: FIELD_DETAILS.TCIN,
  [FIELD_DETAILS.UPC.keyName]: FIELD_DETAILS.UPC,
  [FIELD_DETAILS.SECONDARY_UPCS.keyName]: FIELD_DETAILS.SECONDARY_UPCS,
  [FIELD_DETAILS.DEPARTMENT_ID.keyName]: {
    ...FIELD_DETAILS.DEPARTMENT_ID,
    tableDetails: {
      ...FIELD_DETAILS.DEPARTMENT_ID.tableDetails,
      defaultColumnWidth: 125,
    },
  },
  [FIELD_DETAILS.CLASS_ID.keyName]: {
    ...FIELD_DETAILS.CLASS_ID,
    tableDetails: {
      ...FIELD_DETAILS.CLASS_ID.tableDetails,
      defaultColumnWidth: 95,
    },
  },
  [FIELD_DETAILS.BRANDS.keyName]: FIELD_DETAILS.BRANDS,
  [FIELD_DETAILS.NEW_END_DATE_INPUT.keyName]: FIELD_DETAILS.NEW_END_DATE_INPUT,
}

export const DATA_TABLE_CONDITIONAL_FIELDS = {
  [FIELD_DETAILS.COST_LOCATION_GROUP_NAME.keyName]:
    FIELD_DETAILS.COST_LOCATION_GROUP_NAME,
}

export const SPREADSHEET_HISTORY_ERROR_SUMMARY_FIELDS = {
  [FIELD_DETAILS.ERROR_MESSAGE.keyName]: {
    ...FIELD_DETAILS.ERROR_MESSAGE,
    variableName: 'errorMessages',
    tableDetails: {
      ...FIELD_DETAILS.ERROR_MESSAGE?.tableDetails,
      defaultColumnWidth: 315,
    },
  },
  [FIELD_DETAILS.VENDOR_ID.keyName]: FIELD_DETAILS.VENDOR_ID,
  [FIELD_DETAILS.COST_LOCATION_GROUP_TYPE.keyName]: {
    ...FIELD_DETAILS.COST_LOCATION_GROUP_TYPE,
    label: 'Location Level',
  },
  [FIELD_DETAILS.COST_LOCATION_GROUP_VALUES.keyName]: {
    ...FIELD_DETAILS.COST_LOCATION_GROUP_VALUES,
    label: 'Location Values',
  },
  [FIELD_DETAILS.UPC.keyName]: {
    ...FIELD_DETAILS.UPC,
    variableName: 'primaryBarcode',
  },
  [FIELD_DETAILS.SECONDARY_UPCS.keyName]: {
    ...FIELD_DETAILS.SECONDARY_UPCS,
    variableName: 'secondaryBarcodes',
  },
  [FIELD_DETAILS.DPCI.keyName]: FIELD_DETAILS.DPCI,
  [FIELD_DETAILS.TCIN.keyName]: FIELD_DETAILS.TCIN,
  [FIELD_DETAILS.ITEM_NAME.keyName]: FIELD_DETAILS.ITEM_NAME,
  [FIELD_DETAILS.BRANDS.keyName]: FIELD_DETAILS.BRANDS,
  [FIELD_DETAILS.COST.keyName]: {
    ...FIELD_DETAILS.COST,
    variableName: 'unitCost',
  },
  [FIELD_DETAILS.CASEPACK_QUANTITY.keyName]: {
    ...FIELD_DETAILS.CASEPACK_QUANTITY,
    variableName: 'casepackQuantity',
  },
  [FIELD_DETAILS.START_DATE.keyName]: FIELD_DETAILS.START_DATE,
  [FIELD_DETAILS.NEW_CASEPACK_COST.keyName]: FIELD_DETAILS.NEW_CASEPACK_COST,
  [FIELD_DETAILS.NEW_UNIT_COST.keyName]: {
    ...FIELD_DETAILS.NEW_UNIT_COST,
    variableName: 'newUnitCost',
  },
}

export const DATA_TABLE_ERROR_SUMMARY_FIELDS = {
  [FIELD_DETAILS.ERROR_MESSAGE.keyName]: {
    ...FIELD_DETAILS.ERROR_MESSAGE,
    variableName: 'errorMessages',
  },
  [FIELD_DETAILS.VENDOR_ID.keyName]: FIELD_DETAILS.VENDOR_ID,
  [FIELD_DETAILS.TCIN.keyName]: FIELD_DETAILS.TCIN,
  [FIELD_DETAILS.COST_LOCATION_GROUP_VALUES.keyName]:
    FIELD_DETAILS.COST_LOCATION_GROUP_TYPE,
  [FIELD_DETAILS.COST_LOCATION_GROUP_TYPE.keyName]:
    FIELD_DETAILS.COST_LOCATION_GROUP_VALUES,
  [FIELD_DETAILS.START_DATE.keyName]: FIELD_DETAILS.START_DATE,
  [FIELD_DETAILS.END_DATE.keyName]: FIELD_DETAILS.END_DATE,
  [FIELD_DETAILS.COST.keyName]: FIELD_DETAILS.COST,
}

export const LOCATION_LEVEL_MODAL_ITEM_FIELDS = {
  [FIELD_DETAILS.DPCI.keyName]: FIELD_DETAILS.DPCI,
  [FIELD_DETAILS.TCIN.keyName]: FIELD_DETAILS.TCIN,
  [FIELD_DETAILS.UPC.keyName]: FIELD_DETAILS.UPC,
  [FIELD_DETAILS.SECONDARY_UPCS.keyName]: FIELD_DETAILS.SECONDARY_UPCS,
  [FIELD_DETAILS.CASEPACK_QUANTITY.keyName]: FIELD_DETAILS.CASEPACK_QUANTITY,
  [FIELD_DETAILS.DEPARTMENT_ID.keyName]: FIELD_DETAILS.DEPARTMENT_ID,
  [FIELD_DETAILS.CLASS_ID.keyName]: FIELD_DETAILS.CLASS_ID,
  [FIELD_DETAILS.BRANDS.keyName]: FIELD_DETAILS.BRANDS,
  [FIELD_DETAILS.ITEM_NAME.keyName]: FIELD_DETAILS.ITEM_NAME,
}

export const LOCATION_LEVEL_MODAL_TABLE_FIELDS = {
  [FIELD_DETAILS.LOCATION_ID.keyName]: FIELD_DETAILS.LOCATION_ID,
  [FIELD_DETAILS.LOCATION_TYPE.keyName]: FIELD_DETAILS.LOCATION_TYPE,
  [FIELD_DETAILS.COST.keyName]: FIELD_DETAILS.COST,
  [FIELD_DETAILS.COST_LOCATION_GROUP_TYPE.keyName]: {
    ...FIELD_DETAILS.COST_LOCATION_GROUP_TYPE,
    tableDetails: {
      ...FIELD_DETAILS.COST_LOCATION_GROUP_TYPE.tableDetails,
      defaultColumnWidth: 135,
    },
  },
  [FIELD_DETAILS.COST_LOCATION_GROUP_VALUES.keyName]:
    FIELD_DETAILS.COST_LOCATION_GROUP_VALUES,
  [FIELD_DETAILS.LAST_UPDATED_ON.keyName]: FIELD_DETAILS.LAST_UPDATED_ON,
  [FIELD_DETAILS.LAST_UPDATED_BY.keyName]: FIELD_DETAILS.LAST_UPDATED_BY,
  [FIELD_DETAILS.NEW_START_DATE_INPUT.keyName]:
    FIELD_DETAILS.NEW_START_DATE_INPUT,
  [FIELD_DETAILS.NEW_END_DATE_INPUT.keyName]: FIELD_DETAILS.NEW_END_DATE_INPUT,
  [FIELD_DETAILS.NEW_UNIT_COST_INPUT.keyName]:
    FIELD_DETAILS.NEW_UNIT_COST_INPUT,
  [FIELD_DETAILS.NEW_CASEPACK_COST_INPUT.keyName]:
    FIELD_DETAILS.NEW_CASEPACK_COST_INPUT,
}

export const COST_CHANGE_INSIGHTS_ITEM_OPTIONS = {
  [FIELD_DETAILS.UPCS.keyName]: FIELD_DETAILS.UPCS,
  [FIELD_DETAILS.DPCIS.keyName]: FIELD_DETAILS.DPCIS,
  [FIELD_DETAILS.TCINS.keyName]: FIELD_DETAILS.TCINS,
  [FIELD_DETAILS.BRANDS.keyName]: FIELD_DETAILS.BRANDS,
  [FIELD_DETAILS.DEPARTMENTS.keyName]: {
    ...FIELD_DETAILS.DEPARTMENTS,
    variableName: 'departmentIds',
  },
  [FIELD_DETAILS.DEPARTMENT_CLASS.keyName]: {
    ...FIELD_DETAILS.DEPARTMENT_CLASS,
    variableName: 'departmentClassIds',
  },
}

export const COST_CHANGE_INSIGHTS_DEFAULT_SELECTIONS = {
  [FIELD_DETAILS.UPCS.variableName]: [],
  [FIELD_DETAILS.DPCIS.variableName]: [],
  [FIELD_DETAILS.TCINS.variableName]: [],
  [FIELD_DETAILS.BRANDS.variableName]: [],
  [COST_CHANGE_INSIGHTS_ITEM_OPTIONS.DEPARTMENTS.variableName]: [],
  [COST_CHANGE_INSIGHTS_ITEM_OPTIONS.DEPARTMENT_CLASS.variableName]: [],
  [FIELD_DETAILS.COST_LOCATION_GROUP_TYPE.variableName]: null,
  [FIELD_DETAILS.COST_LOCATION_GROUP_VALUES.variableName]: [],
  [FIELD_DETAILS.START_DATE_BETWEEN.variableName]: null,
  [FIELD_DETAILS.SUBMITTED_DATE_BETWEEN.variableName]: null,
  [FIELD_DETAILS.DECIDED_DATE_BETWEEN.variableName]: null,
  [FIELD_DETAILS.COST_ORIGIN_SOURCE.variableName]: [],
  [FIELD_DETAILS.APPROVAL_STATUS.variableName]: [],
}

export const GET_COST_CHANGE_INSIGHTS_PENDING_APPROVALS_DEFAULT_SELECTIONS =
  () => {
    return {
      ...COST_CHANGE_INSIGHTS_DEFAULT_SELECTIONS,
      [FIELD_DETAILS.APPROVAL_STATUS.variableName]: [
        APPROVAL_STATUS_ENUMS.AWAITING_MANUAL_REVIEW.enum,
      ],
    }
  }

export const GET_COST_CHANGE_INSIGHTS_REJECTED_COSTS_DEFAULT_SELECTIONS =
  () => {
    return {
      ...COST_CHANGE_INSIGHTS_DEFAULT_SELECTIONS,
      [FIELD_DETAILS.APPROVAL_STATUS.variableName]: [
        APPROVAL_STATUS_ENUMS.AUTOMATICALLY_REJECTED.enum,
        APPROVAL_STATUS_ENUMS.REJECTED_BY_TARGET_EMPLOYEE.enum,
      ],
    }
  }

export const GET_COST_CHANGE_INSIGHTS_SPREADSHEET_LOOKUP_DEFAULT_SELECTIONS =
  () => {
    return {
      ...COST_CHANGE_INSIGHTS_DEFAULT_SELECTIONS,
      [FIELD_DETAILS.COST_ORIGIN_SOURCE.variableName]: ['SPREADSHEET_UPLOAD'],
    }
  }

export const COST_CHANGE_INSIGHTS_SUBMENU_DETAILS_MAP = {
  '/insights': {
    GET_DEFAULT_SELECTIONS: () => COST_CHANGE_INSIGHTS_DEFAULT_SELECTIONS,
    SUBMENU_TEXT: null,
    HIGHLIGHTED_FIELDS: [],
  },
  '/insights/costs-awaiting-approval': {
    GET_DEFAULT_SELECTIONS: () =>
      GET_COST_CHANGE_INSIGHTS_PENDING_APPROVALS_DEFAULT_SELECTIONS(),
    SUBMENU_TEXT: (
      <>
        <span>
          This insights template is intended to help you see all of your cost
          submissions that are still awaiting manual review by a Target
          employee. This template includes a defaulted filter on Approval
          Status, which is all that is needed to return results of only cost
          submissions that were submitted for the currently represented vendor
          and are still awaiting manual review. If you see “No items matching
          the provided filter criteria were found” in the results table after
          searching, that indicates that there are no cost submissions for the
          currently represented vendor that are still waiting for manual review.
        </span>
      </>
    ),
    HIGHLIGHTED_FIELDS: [],
    SUBMENU_NAME: 'Costs Awaiting Approval',
  },
  '/insights/rejected-costs': {
    GET_DEFAULT_SELECTIONS: () =>
      GET_COST_CHANGE_INSIGHTS_REJECTED_COSTS_DEFAULT_SELECTIONS(),
    SUBMENU_TEXT: (
      <>
        <span>
          This insights template is intended to help you see all of your cost
          submissions that were rejected. This template includes defaulted
          filters on Approval Status, which is all that is needed to return
          results of only cost submissions that were submitted for the currently
          represented vendor and were either automatically rejected by the ICM
          system or manually rejected by a Target employee. If you see “No items
          matching the provided filter criteria were found” in the results table
          after searching, that indicates that there are no cost submissions for
          the currently represented vendor that were rejected.
        </span>
      </>
    ),
    HIGHLIGHTED_FIELDS: [],
    SUBMENU_NAME: 'Rejected Costs',
  },
  '/insights/spreadsheet-lookup': {
    GET_DEFAULT_SELECTIONS: () =>
      GET_COST_CHANGE_INSIGHTS_SPREADSHEET_LOOKUP_DEFAULT_SELECTIONS(),
    SUBMENU_TEXT: (
      <>
        <span>
          This insights template is intended to help guide you towards finding
          all the cost submission records and their statuses for a particular
          spreadsheet upload. This template includes a defaulted filter to only
          show costs that were submitted through spreadsheets. Also take note of
          the highlighted "Submitted Between Dates" field. Filtering on this
          field is still optional, but it can be used to narrow the results down
          to a single spreadsheet upload if you know the date range in which it
          was uploaded.
        </span>
      </>
    ),
    HIGHLIGHTED_FIELDS: [FIELD_DETAILS.SUBMITTED_DATE_BETWEEN.keyName],
    SUBMENU_NAME: 'Spreadsheet Lookup',
  },
}

export const COST_CHANGE_INSIGHTS_LOCATION_OPTIONS = {
  [FIELD_DETAILS.COST_LOCATION_GROUP_TYPE.keyName]: {
    ...FIELD_DETAILS.COST_LOCATION_GROUP_TYPE,
    filterType: 'SELECT',
  },
  [FIELD_DETAILS.COST_LOCATION_GROUP_VALUES.keyName]: {
    ...FIELD_DETAILS.COST_LOCATION_GROUP_VALUES,
    filterType: 'AUTOCOMPLETE',
  },
}

export const COST_CHANGE_INSIGHTS_VIEW_BY_OPTIONS = {
  [FIELD_DETAILS.CHAIN.keyName]: FIELD_DETAILS.CHAIN,
  [FIELD_DETAILS.STATE.keyName]: FIELD_DETAILS.STATE,
  [FIELD_DETAILS.DC.keyName]: FIELD_DETAILS.DC,
  [FIELD_DETAILS.CUSTOM.keyName]: FIELD_DETAILS.CUSTOM,
  [FIELD_DETAILS.LOCATION.keyName]: FIELD_DETAILS.LOCATION,
}

export const DVS_COST_CHANGE_INSIGHTS_VIEW_BY_OPTIONS = {
  [FIELD_DETAILS.SUPPLIER_CHAIN.keyName]: FIELD_DETAILS.SUPPLIER_CHAIN,
  [FIELD_DETAILS.SUPPLIER_STATE.keyName]: FIELD_DETAILS.SUPPLIER_STATE,
  [FIELD_DETAILS.SUPPLIER_LOCATION.keyName]: FIELD_DETAILS.SUPPLIER_LOCATION,
}

export const COST_CHANGE_INSIGHTS_TIMEFRAME_OPTIONS = {
  [FIELD_DETAILS.START_DATE_BETWEEN.keyName]: FIELD_DETAILS.START_DATE_BETWEEN,
  [FIELD_DETAILS.SUBMITTED_DATE_BETWEEN.keyName]:
    FIELD_DETAILS.SUBMITTED_DATE_BETWEEN,
  [FIELD_DETAILS.DECIDED_DATE_BETWEEN.keyName]:
    FIELD_DETAILS.DECIDED_DATE_BETWEEN,
}

export const COST_CHANGE_INSIGHTS_MISC_OPTIONS = {
  [FIELD_DETAILS.COST_ORIGIN_SOURCE.keyName]: FIELD_DETAILS.COST_ORIGIN_SOURCE,
  [FIELD_DETAILS.APPROVAL_STATUS.keyName]: FIELD_DETAILS.APPROVAL_STATUS,
}

export const COST_CHANGE_INSIGHTS_ITEM_RESULTS = {
  [FIELD_DETAILS.BRANDS.keyName]: FIELD_DETAILS.BRANDS,
  [FIELD_DETAILS.CLASS_ID.keyName]: FIELD_DETAILS.CLASS_ID,
  [FIELD_DETAILS.DEPARTMENT_ID.keyName]: FIELD_DETAILS.DEPARTMENT_ID,
  [FIELD_DETAILS.DPCI.keyName]: FIELD_DETAILS.DPCI,
  [FIELD_DETAILS.ITEM_NAME.keyName]: FIELD_DETAILS.ITEM_NAME,
  [FIELD_DETAILS.SECONDARY_UPCS.keyName]: FIELD_DETAILS.SECONDARY_UPCS,
  [FIELD_DETAILS.STYLE_ID.keyName]: FIELD_DETAILS.STYLE_ID,
  [FIELD_DETAILS.TCIN.keyName]: FIELD_DETAILS.TCIN,
  [FIELD_DETAILS.UPCS.keyName]: {
    ...FIELD_DETAILS.UPCS,
    variableName: 'primaryUpcs',
  },
}

export const COST_CHANGE_INSIGHTS_SUBMISSION_RESULTS = {
  [FIELD_DETAILS.SUBMITTED_ON.keyName]: FIELD_DETAILS.SUBMITTED_ON,
  [FIELD_DETAILS.COST_LOCATION_GROUP_TYPE.keyName]:
    FIELD_DETAILS.COST_LOCATION_GROUP_TYPE,
  [FIELD_DETAILS.COST_LOCATION_GROUP_VALUES.keyName]:
    FIELD_DETAILS.COST_LOCATION_GROUP_VALUES,
  [FIELD_DETAILS.VENDOR_ID.keyName]: FIELD_DETAILS.VENDOR_ID,
  [FIELD_DETAILS.APPROVAL_STATUS.keyName]: FIELD_DETAILS.APPROVAL_STATUS,
  [FIELD_DETAILS.APPROVAL_STATUS_LABEL.keyName]:
    FIELD_DETAILS.APPROVAL_STATUS_LABEL,
  [FIELD_DETAILS.NEW_UNIT_COST.keyName]: {
    ...FIELD_DETAILS.NEW_UNIT_COST,
    variableName: 'costAmount',
  },
  [FIELD_DETAILS.START_DATE.keyName]: {
    ...FIELD_DETAILS.START_DATE,
    label: 'Effective Date',
  },
  [FIELD_DETAILS.END_DATE.keyName]: FIELD_DETAILS.END_DATE,
  [FIELD_DETAILS.SUBMITTED_BY.keyName]: FIELD_DETAILS.SUBMITTED_BY,
  [FIELD_DETAILS.SOURCE.keyName]: FIELD_DETAILS.SOURCE,
  [FIELD_DETAILS.SOURCE_LABEL.keyName]: FIELD_DETAILS.SOURCE_LABEL,
  [FIELD_DETAILS.DECISION_DATE.keyName]: FIELD_DETAILS.DECISION_DATE,
  [FIELD_DETAILS.DECISION_REASON.keyName]: FIELD_DETAILS.DECISION_REASON,
  [FIELD_DETAILS.APPROVED_BY.keyName]: FIELD_DETAILS.APPROVED_BY,
}

export const MANAGE_CLG_GROUP_LEVEL_OPTIONS = {
  [FIELD_DETAILS.STATE.keyName]: FIELD_DETAILS.STATE,
  [FIELD_DETAILS.DC.keyName]: FIELD_DETAILS.DC,
  [FIELD_DETAILS.LOCATION.keyName]: FIELD_DETAILS.LOCATION,
}

export const MANAGE_NOTIFICATION_RECORDS_FIELDS = {
  [FIELD_DETAILS.VENDOR_IDS.keyName]: FIELD_DETAILS.VENDOR_IDS,
  [FIELD_DETAILS.TIMEZONE.keyName]: FIELD_DETAILS.TIMEZONE,
}

const MANAGE_NOTIFICATIONS_OPTIONS = {
  CT: {
    keyName: 'CT',
    variableName: 'CT',
    label: 'Central Time',
  },
  IST: {
    keyName: 'IST',
    variableName: 'IST',
    label: 'India Standard Time',
  },
  PENDING_MANUAL_REVIEW: {
    keyName: 'PENDING_MANUAL_REVIEW',
    variableName: 'PENDING_MANUAL_REVIEW',
    label: 'Pending Review Cost Changes',
  },
  APPROVED: {
    keyName: 'APPROVED',
    variableName: 'APPROVED',
    label: 'Approved Costs',
  },
  REJECTED: {
    keyName: 'REJECTED',
    variableName: 'REJECTED',
    label: 'Rejected Costs',
  },
  EFFECTIVE: {
    keyName: 'EFFECTIVE',
    variableName: 'EFFECTIVE',
    label: 'Effective Costs',
  },
}

export const MANAGE_NOTIFICATION_EVENT_FIELDS = {
  [MANAGE_NOTIFICATIONS_OPTIONS.PENDING_MANUAL_REVIEW.keyName]:
    MANAGE_NOTIFICATIONS_OPTIONS.PENDING_MANUAL_REVIEW,
  [MANAGE_NOTIFICATIONS_OPTIONS.APPROVED.keyName]:
    MANAGE_NOTIFICATIONS_OPTIONS.APPROVED,
  [MANAGE_NOTIFICATIONS_OPTIONS.REJECTED.keyName]:
    MANAGE_NOTIFICATIONS_OPTIONS.REJECTED,
  [MANAGE_NOTIFICATIONS_OPTIONS.EFFECTIVE.keyName]:
    MANAGE_NOTIFICATIONS_OPTIONS.EFFECTIVE,
}

export const MANAGE_NOTIFICATION_TIMEZONE_FIELDS = {
  [MANAGE_NOTIFICATIONS_OPTIONS.CT.keyName]: MANAGE_NOTIFICATIONS_OPTIONS.CT,
  [MANAGE_NOTIFICATIONS_OPTIONS.IST.keyName]: MANAGE_NOTIFICATIONS_OPTIONS.IST,
}

export const CLG_OPTIONS_TO_SHOW = {
  pending: 'PENDING',
  dvs: 'DVS',
  standard: 'STANDARD',
}
